import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { Observable } from 'rxjs/Rx';
import { SxHttpService } from './sxHttp.service';
import { AzureFile } from '../model/interfaces/azureFile.Interface';
import {
  ProductEmail,
  ProductEmailAttribute,
  ProductEmailAttachment,
  ProductEmailAttachmentData,
  ProductEmailCategories,
  ProductEmailStandardDictionary,
} from '../model/classes/productEmail.class';
import { TemplateListadoItem } from '../model/classes/templateListadoItem.class';
import { StandardFileContentModel } from './azureFileManager.service';
import { EntityChildsContext } from 'app/private/entity/entityWithChilds/model/EntityChildsContext';

@Injectable()
export class ProductEmailService {
  private serviceUrl = 'api/productEmail';
  private subscriptionUrl = 'api/productEmailSubscription';
  azureFile: AzureFile;

  constructor(private http: Http, private sxHttp: SxHttpService) {}

  /**
   * Get all product emails
   * @param systemCode
   * @param businessCode
   * @param productCode
   */
  getAll(
    systemCode: string,
    businessCode: string,
    productCode: string
  ): Observable<TemplateListadoItem[]> {
    const url = `${this.serviceUrl}/${systemCode}/${businessCode}/${productCode}`;
    return this.sxHttp.get(url).map((response) => {
      const emails = response.json().data;
      return emails.map((email) => {
        return {
          id: email.id,
          imagen: email.urlImage,
          titulo: email.description,
          subtitulo: email.descriptionExt,
        };
      });
    });
  }

  /**
   * Get all product emails as TemplateListadoItem
   * @param systemCode
   * @param businessCode
   * @param productCode
   */
  getTemplateList(): Observable<TemplateListadoItem[]> {
    const url = `${this.serviceUrl}`;
    return this.sxHttp.get(url).map((response) => {
      const emails = response.json().data;
      return emails.map((email) => {
        return {
          id: email.id,
          imagen: email.urlImage,
          titulo: email.description,
          subtitulo: email.descriptionExt,
        };
      });
    });
  }

  /**
   * Get product email
   * @param systemCode
   * @param businessCode
   * @param productCode
   * @param emailCode
   */
  get(id: string): Observable<ProductEmail> {
    const url = `${this.serviceUrl}/${id}`;
    return this.sxHttp
      .get(url)
      .map((response) => response.json().data as ProductEmail);
  }
  getCategories(): Observable<ProductEmailCategories[]> {
    const url = `${this.serviceUrl}/getcategories`;
    return this.sxHttp
      .get(url)
      .map((response) => response.json().data as ProductEmailCategories[]);
  }
  getStandardDictionary(): Observable<ProductEmailStandardDictionary[]> {
    const url = `${this.serviceUrl}/getstandarddictionary`;
    return this.sxHttp
      .get(url)
      .map(
        (response) => response.json().data as ProductEmailStandardDictionary[]
      );
  }

  /**
   * Save/Update product email
   * @param email
   */
  save(email: ProductEmail): Promise<ProductEmail> {
    const url = `${this.serviceUrl}`;
    return this.sxHttp
      .post(url, email)
      .toPromise()
      .then((response) => response.json().data as ProductEmail);
  }

  /**
   * Delete product email
   * @param systemCode
   * @param businessCode
   * @param productCode
   * @param emailCode
   */
  delete(
    systemCode: string,
    businessCode: string,
    productCode: string,
    emailCode: string
  ): Promise<Boolean> {
    const url = `${this.serviceUrl}/${systemCode}/${businessCode}/${productCode}/${emailCode}`;
    return this.sxHttp
      .delete(url)
      .toPromise()
      .then((result) => result.json().data as Boolean);
  }

  /**
   *  Create product email rule
   * @param email
   * @param ruleName
   */
  createRule(email: ProductEmail, ruleName: string): Promise<number> {
    const url = `${this.serviceUrl}/CreateRule`;

    const postRule = {
      email: email,
      ruleName: ruleName,
    };

    return this.sxHttp
      .post(url, postRule)
      .toPromise()
      .then((response) => response.json().data as number);
  }

  /**
   * Delete product email rule
   * @param email
   * @param ruleId
   */
  deleteRule(email: ProductEmail, ruleId: number): Promise<Boolean> {
    const url = `${this.serviceUrl}/${email.systemCode}/${email.businessCode}/${email.productCode}/${email.emailCode}/DeleteRule/${ruleId}`;
    return this.sxHttp
      .delete(url)
      .toPromise()
      .then((result) => result.json().data as Boolean);
  }

  /**
   * Get product email attribute
   * @param systemCode
   * @param businessCode
   * @param productCode
   * @param emailCode
   * @param attributeId
   */
  getAttribute(
    systemCode: string,
    businessCode: string,
    productCode: string,
    emailCode: string,
    attributeId: number
  ): Observable<ProductEmailAttribute> {
    const url = `${this.serviceUrl}/${systemCode}/${businessCode}/${productCode}/${emailCode}/Attribute/${attributeId}`;
    return this.sxHttp
      .get(url)
      .map((response) => response.json().data as ProductEmailAttribute);
  }

  /**
   * Save product email attribute
   * @param systemCode
   * @param businessCode
   * @param productCode
   * @param emailCode
   * @param attributeId
   * @param attribute
   */
  saveAttribute(
    systemCode: string,
    businessCode: string,
    productCode: string,
    emailCode: string,
    attributeId: number,
    attribute: ProductEmailAttribute
  ): Promise<ProductEmailAttribute> {
    const url = `${this.serviceUrl}/${systemCode}/${businessCode}/${productCode}/${emailCode}/Attribute/${attributeId}`;
    return this.sxHttp
      .post(url, attribute)
      .toPromise()
      .then((response) => response.json().data as ProductEmailAttribute);
  }

   /**
   * Create product email attribute
   * @param systemCode
   * @param businessCode
   * @param productCode
   * @param emailId
   * @param attributeId
   * @param attribute
   */
    createAttribute(
      systemCode: string,
      businessCode: string,
      productCode: string,
      emailId: string,
      attribute: ProductEmailAttribute
    ): Promise<ProductEmailAttribute> {
      const url = `${this.serviceUrl}/${systemCode}/${businessCode}/${productCode}/${emailId}/Attribute`;
      return this.sxHttp
        .post(url, attribute)
        .toPromise()
        .then((response) => response.json().data as ProductEmailAttribute);
    }

  /**
   * Test the email configuration
   * @param email
   * @param documentId
   */
  testConfiguration(email: ProductEmail, documentId: number): Promise<Boolean> {
    const url = `${this.serviceUrl}/${email.systemCode}/${email.businessCode}/${email.productCode}/${email.emailCode}/TestConfiguration/${documentId}`;
    return this.sxHttp
      .delete(url)
      .toPromise()
      .then((result) => result.json().data as Boolean);
  }

  downloadTemplate(
    id: string
  ): Observable<String> {
    const url = `${this.serviceUrl}/${id}/DownloadTemplate`;
    return this.sxHttp
      .get(url)
      .map((response) => response.json().data as String);
  }

  getFileData(file: ProductEmailAttachment) {
    const url = `api/productEmail/GetAttachmentData?azurePath=${file.azurePath}`;
    return this.sxHttp
      .get(url)
      .map((response) => response.json().data as ProductEmailAttachmentData);
  }

  //#region Product Report Subscriptions
  getAllWithContext(
    organizatinCode: string,
    subscriptionCode: string,
    channelCode: string,
    productCode: string
  ): Observable<ProductEmail[]> {
    const context: EntityChildsContext = {
      organizationCode: organizatinCode,
      subscriptionCode: subscriptionCode,
      channelCode: channelCode,
      productCode,
    };
    return this.sxHttp
      .post(this.subscriptionUrl + '/GetProductEmail', context)
      .map((response) => response.json().data as ProductEmail[]);
  }

  getAllSubscribed(
    organizationCode: string,
    subscriptionCode: string,
    channelCode: string,
    productCode: string
  ): Observable<ProductEmail[]> {
    let context: EntityChildsContext = {
      organizationCode: organizationCode,
      subscriptionCode: subscriptionCode,
      channelCode: channelCode,
      productCode: productCode,
    };
    return this.sxHttp
      .post(`${this.subscriptionUrl}/GetSubscription`, context)
      .map((response) => {
        return response.json().data as ProductEmail[];
      });
  }

  productEmailSubscription(productEmailSubscription): Observable<Boolean> {
    return this.sxHttp
      .post(`${this.subscriptionUrl}/Subscription`, productEmailSubscription)
      .map((response) => {
        return response.json().data;
      });
  }

  unsubscribe(
    idProductEmail: string,
    organizationCode: string,
    subscriptionCode: string,
    channelCode: string,
    productCode: string
  ): Promise<Boolean> {
    let context: EntityChildsContext = {
      organizationCode: organizationCode,
      subscriptionCode: subscriptionCode,
      channelCode: channelCode,
      productCode: productCode,
    };
    return this.sxHttp
      .post(this.subscriptionUrl + '/Delete' + '/' + idProductEmail, context)
      .toPromise()
      .then((result) => result.json().data);
  }
  //endregion
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/infrastructure/services/productEmail.service.ts